import { useEffect, useState } from "react";
import { Mail, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";

const TRIGGER_BADGE = {
  "Event-driven (on lens submission)": "bg-rose-100 text-rose-800",
  "Cron (Mon 09:00 UTC)": "bg-violet-100 text-violet-800",
  "Cron (Fri 16:00 UTC)": "bg-violet-100 text-violet-800",
  "Cron (daily 06:30 UTC)": "bg-amber-100 text-amber-800",
  "Cron (existing schedule)": "bg-slate-100 text-slate-700",
  "Cron (per-tenant schedule)": "bg-slate-100 text-slate-700",
};

export function OwnerNotificationsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/owner/notification-prefs")
      .then(({ data: d }) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled)
          toast.error(
            formatApiErrorDetail(e.response?.data?.detail) ||
              "Failed to load notification preferences",
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = async (key, currentValue) => {
    setPending(key);
    try {
      const { data: updated } = await api.patch("/owner/notification-prefs", {
        key,
        value: !currentValue,
      });
      setData((d) => ({ ...d, prefs: updated.prefs }));
      toast.success(
        !currentValue ? `Enabled: ${key}` : `Disabled: ${key}`,
      );
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || "Toggle failed",
      );
    } finally {
      setPending(null);
    }
  };

  const testSend = async (key) => {
    setPending(`test:${key}`);
    try {
      const { data: res } = await api.post(
        `/owner/notification-prefs/${key}/test-send`,
      );
      toast.success(`Test email sent to ${res.sent_to}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Test-send failed");
    } finally {
      setPending(null);
    }
  };

  if (loading) {
    return (
      <section
        className="mt-6 rounded-2xl border border-slate-200 bg-white p-5"
        data-testid="owner-notifications-loading"
      >
        <Loader2 className="animate-spin text-slate-400" size={18} />
      </section>
    );
  }
  if (!data) return null;

  const enabledCount = Object.values(data.prefs || {}).filter(Boolean).length;
  const totalCount = (data.catalogue || []).length;

  return (
    <section
      className="mt-6 rounded-2xl border border-slate-200 bg-white p-5"
      data-testid="owner-notifications-card"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Mail size={16} className="text-violet-600" />
          <h2 className="font-display text-base font-semibold text-ek-ink">
            Owner notifications
          </h2>
        </div>
        <span
          className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-ek-ink/70"
          data-testid="owner-notifications-count"
        >
          {enabledCount} of {totalCount} enabled
        </span>
      </div>
      <p className="mt-1 text-xs text-ek-ink/70">
        All auto-emails are <strong>OFF by default in pre-pilot phase</strong>.
        Flip each one on only when you're ready to receive its output. Changes
        take effect immediately for event-driven sends; scheduled jobs read the
        toggle at next fire.
      </p>
      <div className="mt-4 divide-y divide-slate-100">
        {(data.catalogue || []).map((item) => {
          const current = !!data.prefs[item.key];
          const isPending = pending === item.key;
          return (
            <div
              key={item.key}
              className="flex items-start justify-between gap-4 py-3"
              data-testid={`notification-row-${item.key}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-ek-ink">{item.label}</p>
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                      TRIGGER_BADGE[item.trigger] || "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {item.trigger}
                  </span>
                </div>
                <p className="mt-1 text-[11.5px] text-ek-ink/60">
                  {item.description}
                </p>
              </div>
              <div className="flex items-center gap-2 pt-1">
                {isPending && (
                  <Loader2 size={14} className="animate-spin text-slate-400" />
                )}
                {current && (
                  <button
                    type="button"
                    onClick={() => testSend(item.key)}
                    disabled={pending === `test:${item.key}`}
                    className="rounded-md border border-violet-300 bg-white px-2 py-0.5 text-[10.5px] font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50"
                    data-testid={`notification-test-${item.key}`}
                    title="Send a preview of this email to your Owner inbox"
                  >
                    {pending === `test:${item.key}` ? "Sending…" : "Test send"}
                  </button>
                )}
                <Switch
                  checked={current}
                  disabled={isPending}
                  onCheckedChange={() => toggle(item.key, current)}
                  data-testid={`notification-toggle-${item.key}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
